import React, { useContext, useEffect, useRef, useState } from "react";
import { PoseLandmarker, NormalizedLandmark } from "@mediapipe/tasks-vision";

import "./Scan.css";
import { LanguageContext } from "contexts";
import {
  Sound,
  ImageBlobAndViewTypeType,
  ProcedureType,
  TText,
  SOUNDTRANSLATION,
  ViewType,
  Language,
  SoundTextMapping,
} from "api/type";

import { poseList, PoseType } from "./poseList";
import CameraAccessError from "./AccessCameraError";
import {
  isFaceProcedure,
  isTorsoProcedure,
} from "utils/isFaceBodyOrTorsoProcedure";
import PrimaryButton from "../PrimaryButton";
import SoundOn from "./SoundOn";
import { getTranslation } from "utils/translate";
import { NormalText } from "components";

// text variables
const text: TText = {
  text1: {
    en: "Adjust your posture following the guidelines.",
    es: "Ajusta tu postura siguiendo las pautas",
  },
  text2: {
    en: "Tilt your device until the line turns green.",
    es: "Inclina tu dispositivo hasta que la línea se vuelva verde.",
  },
  placeYourDevice: {
    en: "Place your device on a flat surface.",
    es: "Coloca tu dispositivo en una superficie plana.",
  },
  adjustYourPosture: {
    en: "Adjust your posture following the guidelines.",
    es: "Ajusta tu postura siguiendo las pautas.",
  },
  turn180DegreesOppositeDirection: {
    en: "Turn 180 degrees in the opposite direction.",
    es: "Gira 180 grados en la dirección opuesta.",
  },
  turn90DegreesLeft: {
    en: "Turn 90 degrees to the left.",
    es: "Gira 90 grados a la izquierda.",
  },
  turn90DegreesLeftBackToCamera: {
    en: "Turn 90 degrees to the left and face your back to the camera.",
    es: "Gira 90 grados a la izquierda y da la espalda a la cámara.",
  },
  turn45DegreesLeft: {
    en: "Turn 45 degrees to the left.",
    es: "Gira 45 grados a la izquierda.",
  },
  faceFrontTurn45DegreesLeft: {
    en: "Face to the front and turn 45 degrees to the left.",
    es: "Mira hacia adelante y gira 45 grados a la izquierda.",
  },
  faceFrontTurn45DegreesRight: {
    en: "Face to the front and turn 45 degrees to the right.",
    es: "Mira hacia adelante y gira 45 grados a la derecha.",
  },
  turn45DegreesRight: {
    en: "Turn 45 degrees to the right.",
    es: "Gira 45 grados a la derecha.",
  },
};

type PosesToTake = {
  isPictureTaken: boolean;
  poseType: PoseType;
};

export type TImageBlobAndViewType = {
  image: Blob;
  viewType: ViewType;
};

type ScanProps = {
  poseLandmarker: PoseLandmarker | "ERROR" | null;
  selectedProcedure: ProcedureType | null;
  handleSaveImageBlobsAndViewTypes: (
    imageBlobsAndViewTypes: TImageBlobAndViewType[],
  ) => void;
  isScanAllPoses?: boolean;
};

const Scan: React.FC<ScanProps> = ({
  poseLandmarker,
  selectedProcedure = null,
  handleSaveImageBlobsAndViewTypes,
  isScanAllPoses = false,
}) => {
  const language = useContext<Language>(LanguageContext)[0];

  const soundRef = useRef<Sound | null>(null);
  const placeYourDeviceRef = useRef<HTMLAudioElement>(null);
  const perfectNowAdjustRef = useRef<HTMLAudioElement>(null);
  const perfectStayStillRef = useRef<HTMLAudioElement>(null);
  const BackToTheFrontAnd45ForFaceRef = useRef<HTMLAudioElement>(null);
  const continueToTurn45LeftRef = useRef<HTMLAudioElement>(null);
  const continueToTurn45LeftRef2 = useRef<HTMLAudioElement>(null);
  const continueToTurn45RightRef = useRef<HTMLAudioElement>(null);
  const continueToTurn90RightRef = useRef<HTMLAudioElement>(null);
  const NowTurn180LeftShoulderToCameraRef = useRef<HTMLAudioElement>(null);
  const NowTurn90LeftBackToCameraRef = useRef<HTMLAudioElement>(null);
  const nowTurn45LeftRef = useRef<HTMLAudioElement>(null);
  const nowTurn90LeftRef = useRef<HTMLAudioElement>(null);
  const LastOneNowTurn90LeftLeftShoulderToCameraRef =
    useRef<HTMLAudioElement>(null);
  const nowAdjustRef = useRef<HTMLAudioElement>(null);
  const wereDoneRef = useRef<HTMLAudioElement>(null);

  const setRef = useRef<Set<Sound>>(new Set());

  const videoRef = useRef<HTMLVideoElement>(null);

  const [landmakers, setLandmakers] = useState<NormalizedLandmark[]>([]);

  const imageBlobsAndViewTypesRef = useRef<ImageBlobAndViewTypeType[]>([]);
  const isTakingPicture = useRef<boolean>(false);
  const [isDoneScanning, setIsDoneScanning] = useState<boolean>(false);

  const [posesToTake, setPosesToTake] = useState<PosesToTake[]>([]);

  const instructionRef = useRef<string>("");
  const captionRef = useRef<string>("");
  const overlaySrcRef = useRef<string>("");

  const currentPoseIndex = useRef<number>(-1);
  const isTimeoutCalled = useRef<boolean>(false);
  const timeOutIds = useRef<NodeJS.Timeout[]>([]);

  const prevSecond = useRef<number>(0);
  const currentSecond = useRef<number>(0);
  const secondInPosition = useRef<number>(0);

  const isSettingUpCamera = useRef<boolean>(true);
  const greenlinePosition = useRef<number>(0);

  const [isErrorOpeningCamera, setIsErrorOpeningCamera] =
    useState<boolean>(false);

  useEffect(() => {
    if (!poseLandmarker) return;
    if (poseLandmarker === "ERROR") return;
    if (!navigator || !navigator.mediaDevices) return;

    let lastVideoTime = -1;
    const predictWebcam = () => {
      if (!videoRef.current) return;

      let startTimeMs = performance.now();
      if (lastVideoTime !== videoRef.current.currentTime) {
        lastVideoTime = videoRef.current.currentTime;
        poseLandmarker.detectForVideo(
          videoRef.current,
          startTimeMs,
          (result) => {
            const newLandmarks = result.landmarks[0] || landmakers;
            setLandmakers([...newLandmarks]);
          },
        );
      }
      window.requestAnimationFrame(predictWebcam);
    };

    navigator.mediaDevices
      .getUserMedia({
        video: { aspectRatio: 1 },
        audio: false,
      })
      .then((stream) => {
        const track = stream.getVideoTracks()[0];
        const maxHeight = stream.getVideoTracks()[0].getCapabilities().height
          ?.max;
        const maxWidth = stream.getVideoTracks()[0].getCapabilities().width
          ?.max;
        if (maxHeight && maxWidth) {
          track
            .applyConstraints({
              width: {
                max: Math.min(maxHeight, maxWidth),
                ideal: Math.min(maxHeight, maxWidth) / 2,
                min: 1,
              },
              height: {
                max: Math.min(maxHeight, maxWidth),
                ideal: Math.min(maxHeight, maxWidth) / 2,
                min: 1,
              },
            })
            .catch(() => {
              setIsErrorOpeningCamera(true);
            });
        }
        track
          .applyConstraints({
            aspectRatio: 1,
          })
          .catch(() => {
            setIsErrorOpeningCamera(true);
          });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.addEventListener("loadeddata", predictWebcam);
          soundRef.current = Sound.PlaceYourDevice;
          placeYourDeviceRef.current?.play();
          if (language === "en") {
            captionRef.current = SoundTextMapping[Sound.PlaceYourDevice];
          } else {
            captionRef.current = SoundTextMapping[Sound.PlaceYourDeviceES];
          }
          setRef.current.add(Sound.PlaceYourDevice);
        }
      })
      .catch((error) => {
        console.error(error);
        setIsErrorOpeningCamera(true);
      });
    // eslint-disable-next-line
  }, [poseLandmarker]);

  useEffect(() => {
    const interval = setInterval(() => {
      currentSecond.current = currentSecond.current + 1;
    }, 1000);
    let currentVideoRef = videoRef.current;

    if (!selectedProcedure) return;
    if (
      selectedProcedure === ProcedureType.BBL ||
      selectedProcedure === ProcedureType.BUTTOCK_AUGMENTATION
    ) {
      if (isScanAllPoses) {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.bodyFrontPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.bodyLeftPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.bodyBackPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.bodyRightPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      } else {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.bodyBackPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      }
      return;
    }
    if (isTorsoProcedure(selectedProcedure)) {
      if (isScanAllPoses) {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.breastFrontPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.breastLeftObliquePose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.breastLeftPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.breastRightPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.breastRightObliquePose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      } else {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.breastFrontPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      }
      return;
    }
    if (isFaceProcedure(selectedProcedure)) {
      if (isScanAllPoses) {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.headFrontPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.headLeftObliquePose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.headLeftPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.headRightObliquePose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.headRightPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      } else {
        setPosesToTake([
          {
            isPictureTaken: false,
            poseType: poseList.headFrontPose,
          },
          {
            isPictureTaken: false,
            poseType: poseList.noPose,
          },
        ]);
      }
      return;
    }
    setPosesToTake([
      {
        isPictureTaken: false,
        poseType: poseList.bodyFrontPose,
      },
      {
        isPictureTaken: false,
        poseType: poseList.noPose,
      },
    ]);
    return () => {
      if (currentVideoRef && currentVideoRef.srcObject) {
        const tracks: MediaStreamTrack[] = (
          currentVideoRef.srcObject as MediaStream
        ).getTracks();
        tracks.forEach((track) => track.stop());
      }
      clearInterval(interval);
    };
  }, [selectedProcedure, isScanAllPoses]);

  const takePicture = async (poseType: PoseType, poseIndex: number) => {
    isTakingPicture.current = false;
    const video = videoRef.current;
    if (!video) return;
    const canvas = new OffscreenCanvas(video.videoWidth, video.videoHeight);
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    try {
      // @ts-ignore comment
      context.drawImage(video, 0, 0);
      // @ts-ignore comment
      canvas.convertToBlob({ type: "image/png" }).then((blob) => {
        if (blob) {
          const currentImageBlobAndViewType: ImageBlobAndViewTypeType = {
            blob: blob,
            viewType: poseType.viewType,
            imageUrl: URL.createObjectURL(blob),
          };
          if (
            imageBlobsAndViewTypesRef.current.length ===
            posesToTake.length - 1
          ) {
            const updatedImageCaptureBlobs = [
              ...imageBlobsAndViewTypesRef.current,
            ];
            updatedImageCaptureBlobs[poseIndex] = currentImageBlobAndViewType;
            imageBlobsAndViewTypesRef.current = updatedImageCaptureBlobs;
          } else
            imageBlobsAndViewTypesRef.current = [
              ...imageBlobsAndViewTypesRef.current,
              currentImageBlobAndViewType,
            ];
        }
      });
    } catch (error) {
      console.error("Error taking picture:", error);
    }

    const updatedPoses = posesToTake.map((pose, index) => {
      if (index === poseIndex) {
        return {
          ...pose,
          isPictureTaken: true,
        };
      }
      return pose;
    });
    setPosesToTake(updatedPoses);
    clearAllTimeOut();
    setTimeout(() => {
      isTakingPicture.current = false;
      isRetakePhotoRef.current = false;
    }, 250);
  };

  const callDetectPoseFunctionTwo = (poseType: PoseType, poseIndex: number) => {
    switch (poseType.viewType) {
      case "TORSO_FRONT":
        detectFrontBreastPose(poseType, poseIndex);
        break;
      case "TORSO_RIGHT":
        detectRightBreastPose(poseType, poseIndex);
        break;
      case "TORSO_RIGHT_45":
        detectRightObliqueBreastPose(poseType, poseIndex);
        break;
      case "TORSO_LEFT":
        detectLeftBreastPose(poseType, poseIndex);
        break;
      case "TORSO_LEFT_45":
        detectLeftObliqueBreastPose(poseType, poseIndex);
        break;
      case "HEAD_RIGHT":
        detectFaceRightPose(poseType, poseIndex);
        break;
      case "HEAD_RIGHT_45":
        detectFaceRightObliquePose(poseType, poseIndex);
        break;
      case "HEAD_FRONT":
        detectFaceFrontPose(poseType, poseIndex);
        break;
      case "HEAD_LEFT":
        detectFaceLeftPose(poseType, poseIndex);
        break;
      case "HEAD_LEFT_45":
        detectFaceLeftObliquePose(poseType, poseIndex);
        break;
      case "BODY_RIGHT":
        detectBodyRightPose(poseType, poseIndex);
        break;
      case "BODY_FRONT":
        detectBodyFrontPose(poseType, poseIndex);
        break;
      case "BODY_LEFT":
        detectBodyLeftPose(poseType, poseIndex);
        break;
      case "BODY_BACK":
        detectBodyBackPose(poseType, poseIndex);
        break;
      case "":
        setIsDoneScanning(true);
        wereDoneRef.current?.play();
        if (language === "en") {
          captionRef.current = SoundTextMapping[Sound.WereDone];
        } else {
          captionRef.current = SoundTextMapping[Sound.WereDoneES];
        }
        break;
      default:
        console.error("Unknown pose type:", poseType);
    }
  };

  const callDetectPoseFunctionOne = () => {
    if (!isTakingPicture.current) {
      for (const [poseIndex, element] of posesToTake.entries()) {
        if (!element.isPictureTaken && !isDoneScanning) {
          callDetectPoseFunctionTwo(element.poseType, poseIndex);
          currentPoseIndex.current = poseIndex;
          break;
        }
      }
    }
  };

  const detectDistance = () => {
    if (!landmakers || landmakers.length === 0 || !selectedProcedure) {
      greenlinePosition.current = 0.99;
      secondInPosition.current = 0;
      return;
    }

    let leftShoulderOrEye;
    let leftHipKneeOrMouth;
    if (isTorsoProcedure(selectedProcedure)) {
      leftShoulderOrEye = landmakers[11];
      leftHipKneeOrMouth = landmakers[23];
    } else if (isFaceProcedure(selectedProcedure)) {
      leftShoulderOrEye = landmakers[2];
      leftHipKneeOrMouth = landmakers[9];
    } else {
      leftShoulderOrEye = landmakers[11];
      leftHipKneeOrMouth = landmakers[25];
    }

    if (
      leftShoulderOrEye.y < 0.05 ||
      leftShoulderOrEye.y > 0.4 ||
      leftHipKneeOrMouth.y > 1.3
    ) {
      greenlinePosition.current = 0.98;
      secondInPosition.current = 0;
      return;
    }

    let heightDistance: any;
    if (isTorsoProcedure(selectedProcedure))
      heightDistance = leftHipKneeOrMouth.y - leftShoulderOrEye.y - 0.1;
    else if (isFaceProcedure(selectedProcedure))
      heightDistance = leftHipKneeOrMouth.y - leftShoulderOrEye.y + 0.37;
    else heightDistance = leftHipKneeOrMouth.y - leftShoulderOrEye.y - 0.25;

    greenlinePosition.current = heightDistance;

    const isInPosition = heightDistance > 0.45 && heightDistance < 0.55;

    if (!isInPosition || soundRef.current) {
      secondInPosition.current = 0;
      return;
    }

    if (currentSecond.current !== prevSecond.current) {
      prevSecond.current = currentSecond.current;
      secondInPosition.current += 1;
    }
    if (secondInPosition.current > 2) {
      isSettingUpCamera.current = false;
      secondInPosition.current = 0;
    }
  };

  useEffect(() => {
    if (isSettingUpCamera && isSettingUpCamera.current) {
      detectDistance();
    } else if (!isTakingPicture.current) callDetectPoseFunctionOne();
    // eslint-disable-next-line
  }, [landmakers]);

  const clearAllTimeOut = () => {
    isTimeoutCalled.current = false;
    timeOutIds.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeOutIds.current = [];
  };

  const [threeSecondCounter, setThreeSecondCounter] = React.useState(0);

  useEffect(() => {
    const timer =
      threeSecondCounter > 0 &&
      setInterval(() => setThreeSecondCounter(threeSecondCounter - 1), 1000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [threeSecondCounter]);

  const startThreeSecondCountingAndTakePicture = async (
    poseType: PoseType,
    poseIndex: number,
  ) => {
    isTakingPicture.current = true;
    setThreeSecondCounter(3);
    setTimeout(() => {
      takePicture(poseType, poseIndex);
    }, 3750);
  };

  ////////////////////////// TORSO //////////////////////////
  const checkFacialOrientation = () => {
    const nose = landmakers[0];
    const leftShoulder = landmakers[11];
    const rightShoulder = landmakers[12];
    const differentShouldersZ = leftShoulder.z - rightShoulder.z;

    const isFaceFront = Math.abs(differentShouldersZ) < 0.3;
    const isFaceLeft =
      Math.abs(rightShoulder.x - leftShoulder.x) < 0.1 &&
      nose.x + nose.x > rightShoulder.x + leftShoulder.x;
    const isFaceRight =
      Math.abs(rightShoulder.x - leftShoulder.x) < 0.1 &&
      nose.x + nose.x < rightShoulder.x + leftShoulder.x;
    const isObliqueLeft = differentShouldersZ > 0;

    return { isFaceFront, isFaceLeft, isFaceRight, isObliqueLeft };
  };

  const inPosition = (poseType: PoseType, poseIndex: number) => {
    overlaySrcRef.current = poseType.validImage;

    if (currentSecond.current !== prevSecond.current) {
      prevSecond.current = currentSecond.current;
      secondInPosition.current += 1;
    }

    if (secondInPosition.current > 2) {
      soundRef.current = Sound.PerfectStayStill;
      perfectStayStillRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.PerfectStayStill];
      } else {
        captionRef.current = SoundTextMapping[Sound.PerfectStayStillES];
      }
      setTimeout(() => {
        overlaySrcRef.current = "";
        secondInPosition.current = 0;
        startThreeSecondCountingAndTakePicture(poseType, poseIndex);
      }, 3000);
    }
  };

  const detectFrontBreastPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.adjustYourPosture[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.PerfectNowAdjust[language] &&
      !setRef.current.has(SOUNDTRANSLATION.PerfectNowAdjust[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.PerfectNowAdjust[language];
      setRef.current.add(SOUNDTRANSLATION.PerfectNowAdjust[language]);
      perfectNowAdjustRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjust];
      } else {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjustES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceFront } = checkFacialOrientation();

    if (!isFaceFront) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectLeftObliqueBreastPose = (
    poseType: PoseType,
    poseIndex: number,
  ) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesLeft[language];
    if (
      language &&
      soundRef.current !== SOUNDTRANSLATION.NowTurn45Left[language] &&
      !setRef.current.has(SOUNDTRANSLATION.NowTurn45Left[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.NowTurn45Left[language];
      setRef.current.add(SOUNDTRANSLATION.NowTurn45Left[language]);
      nowTurn45LeftRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.NowTurn45Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.NowTurn45LeftES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceFront, isFaceLeft, isFaceRight, isObliqueLeft } =
      checkFacialOrientation();

    if (isFaceFront || isFaceRight || isFaceLeft || !isObliqueLeft) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectLeftBreastPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesLeft[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.ContinueToTurn45Left[language] &&
      !setRef.current.has(SOUNDTRANSLATION.ContinueToTurn45Left[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.ContinueToTurn45Left[language];
      setRef.current.add(SOUNDTRANSLATION.ContinueToTurn45Left[language]);
      continueToTurn45LeftRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45LeftES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceFront, isFaceLeft, isFaceRight } = checkFacialOrientation();

    if (isFaceFront || !isFaceLeft || isFaceRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectRightObliqueBreastPose = (
    poseType: PoseType,
    poseIndex: number,
  ) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesLeft[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.ContinueToTurn45Left2[language] &&
      !setRef.current.has(SOUNDTRANSLATION.ContinueToTurn45Left2[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.ContinueToTurn45Left2[language];
      setRef.current.add(SOUNDTRANSLATION.ContinueToTurn45Left2[language]);
      continueToTurn45LeftRef2.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45LeftES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceFront, isFaceLeft, isFaceRight, isObliqueLeft } =
      checkFacialOrientation();

    if (isFaceFront || isFaceLeft || isFaceRight || isObliqueLeft) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectRightBreastPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn180DegreesOppositeDirection[language];
    if (
      soundRef.current !==
        SOUNDTRANSLATION.NowTurn180LeftShoulderToCamera[language] &&
      !setRef.current.has(
        SOUNDTRANSLATION.NowTurn180LeftShoulderToCamera[language],
      )
    ) {
      soundRef.current =
        SOUNDTRANSLATION.NowTurn180LeftShoulderToCamera[language];
      setRef.current.add(
        SOUNDTRANSLATION.NowTurn180LeftShoulderToCamera[language],
      );
      NowTurn180LeftShoulderToCameraRef.current?.play();
      if (language === "en") {
        captionRef.current =
          SoundTextMapping[Sound.NowTurn180LeftShoulderToCamera];
      } else {
        captionRef.current =
          SoundTextMapping[Sound.NowTurn180LeftShoulderToCameraES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceFront, isFaceLeft, isFaceRight } = checkFacialOrientation();

    if (isFaceFront || isFaceLeft || !isFaceRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };
  // ////////////////////////// TORSO //////////////////////////

  // ////////////////////////// BODY //////////////////////////
  const checkFacialOrientationBody = () => {
    const nose = landmakers[0];
    const leftShoulder = landmakers[11];
    const rightShoulder = landmakers[12];
    const differentShouldersZ = Math.abs(rightShoulder.z - leftShoulder.z);

    const isFaceFront = differentShouldersZ < 0.2;
    const isFaceBack =
      differentShouldersZ < 0.2 && leftShoulder.x < rightShoulder.x;
    const isFaceRight =
      Math.abs(rightShoulder.x - leftShoulder.x) < 0.09 &&
      nose.x + nose.x < rightShoulder.x + leftShoulder.x;
    const isFaceLeft =
      Math.abs(rightShoulder.x - leftShoulder.x) < 0.09 &&
      nose.x + nose.x > rightShoulder.x + leftShoulder.x;

    return { isFaceBack, isFaceFront, isFaceLeft, isFaceRight };
  };

  const detectBodyFrontPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.adjustYourPosture[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.PerfectNowAdjust[language] &&
      !setRef.current.has(SOUNDTRANSLATION.PerfectNowAdjust[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.PerfectNowAdjust[language];
      setRef.current.add(SOUNDTRANSLATION.PerfectNowAdjust[language]);
      perfectNowAdjustRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjust];
      } else {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjustES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }
    const { isFaceBack, isFaceFront, isFaceLeft, isFaceRight } =
      checkFacialOrientationBody();

    if (!isFaceFront || isFaceBack || isFaceLeft || isFaceRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectBodyLeftPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn90DegreesLeft[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.NowTurn90Left[language] &&
      !setRef.current.has(SOUNDTRANSLATION.NowTurn90Left[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.NowTurn90Left[language];
      setRef.current.add(SOUNDTRANSLATION.NowTurn90Left[language]);
      nowTurn90LeftRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.NowTurn90Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.NowTurn90LeftES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceBack, isFaceFront, isFaceLeft, isFaceRight } =
      checkFacialOrientationBody();

    if (isFaceBack || isFaceFront || !isFaceLeft || isFaceRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectBodyRightPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn90DegreesLeft[language];
    if (
      soundRef.current !==
        SOUNDTRANSLATION.LastOneNowTurn90LeftLeftShoulderToCamera[language] &&
      !setRef.current.has(
        SOUNDTRANSLATION.LastOneNowTurn90LeftLeftShoulderToCamera[language],
      )
    ) {
      soundRef.current =
        SOUNDTRANSLATION.LastOneNowTurn90LeftLeftShoulderToCamera[language];
      setRef.current.add(
        SOUNDTRANSLATION.LastOneNowTurn90LeftLeftShoulderToCamera[language],
      );
      LastOneNowTurn90LeftLeftShoulderToCameraRef.current?.play();
      if (language === "en") {
        captionRef.current =
          SoundTextMapping[Sound.LastOneNowTurn90LeftLeftShoulderToCamera];
      } else {
        captionRef.current =
          SoundTextMapping[Sound.LastOneNowTurn90LeftLeftShoulderToCameraES];
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceBack, isFaceFront, isFaceLeft, isFaceRight } =
      checkFacialOrientationBody();

    if (isFaceBack || isFaceFront || !isFaceRight || isFaceLeft) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectBodyBackPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.adjustYourPosture[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.PerfectNowAdjust[language] &&
      !setRef.current.has(SOUNDTRANSLATION.PerfectNowAdjust[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.PerfectNowAdjust[language];
      setRef.current.add(SOUNDTRANSLATION.PerfectNowAdjust[language]);
      perfectNowAdjustRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjust];
      } else {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjustES];
      }
    }

    // NOTE: This is a special case where the user is facing the camera
    // TODO: Refactor this to remove this special case
    if (isScanAllPoses) {
      instructionRef.current = text.turn90DegreesLeftBackToCamera[language];
      if (
        soundRef.current !==
          SOUNDTRANSLATION.NowTurn90LeftBackToCamera[language] &&
        !setRef.current.has(
          SOUNDTRANSLATION.NowTurn90LeftBackToCamera[language],
        )
      ) {
        soundRef.current = SOUNDTRANSLATION.NowTurn90LeftBackToCamera[language];
        setRef.current.add(
          SOUNDTRANSLATION.NowTurn90LeftBackToCamera[language],
        );
        nowTurn90LeftRef.current?.play();
        if (language === "en") {
          captionRef.current =
            SoundTextMapping[Sound.NowTurn90LeftBackToCamera];
        } else {
          captionRef.current =
            SoundTextMapping[Sound.NowTurn90LeftBackToCameraES];
        }
      }
    }

    if (!landmakers || landmakers.length === 0) {
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    const { isFaceBack, isFaceLeft, isFaceRight } =
      checkFacialOrientationBody();

    if (!isFaceBack || isFaceRight || isFaceLeft) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };
  // ////////////////////////// BODY //////////////////////////

  // ////////////////////////// FACE //////////////////////////
  const detectFaceFrontPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.adjustYourPosture[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.PerfectNowAdjust[language] &&
      !setRef.current.has(SOUNDTRANSLATION.PerfectNowAdjust[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.PerfectNowAdjust[language];
      setRef.current.add(SOUNDTRANSLATION.PerfectNowAdjust[language]);
      perfectNowAdjustRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjust];
      } else {
        captionRef.current = SoundTextMapping[Sound.PerfectNowAdjustES];
      }
    }

    const nose = landmakers[0];
    const leftEar = landmakers[7];
    const rightEar = landmakers[8];

    const isFacingFront =
      Math.abs(leftEar.x - nose.x - (nose.x - rightEar.x)) < 0.03;

    if (!isFacingFront) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectFaceLeftObliquePose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesLeft[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.NowTurn45Left[language] &&
      !setRef.current.has(SOUNDTRANSLATION.NowTurn45Left[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.NowTurn45Left[language];
      setRef.current.add(SOUNDTRANSLATION.NowTurn45Left[language]);
      nowTurn45LeftRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.NowTurn45Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.NowTurn45LeftES];
      }
    }

    const nose = landmakers[0];
    const leftEar = landmakers[7];

    const isRight = leftEar.x - nose.x > -0.1;
    const isLeft = leftEar.x - nose.x < 0.035;

    if (!isLeft || !isRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectFaceLeftPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesLeft[language];
    if (
      soundRef.current !== SOUNDTRANSLATION.ContinueToTurn45Left[language] &&
      !setRef.current.has(SOUNDTRANSLATION.ContinueToTurn45Left[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.ContinueToTurn45Left[language];
      setRef.current.add(SOUNDTRANSLATION.ContinueToTurn45Left[language]);
      continueToTurn45LeftRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45Left];
      } else {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45LeftES];
      }
    }

    const leftEyeOuter = landmakers[1];
    const leftEyeInner = landmakers[3];

    const isFacingLeft = leftEyeInner.x - leftEyeOuter.x < 0.009;

    if (!isFacingLeft) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectFaceRightObliquePose = (
    poseType: PoseType,
    poseIndex: number,
  ) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.faceFrontTurn45DegreesRight[language];
    if (
      soundRef.current !==
        SOUNDTRANSLATION.BackToTheFrontAnd45ForFace[language] &&
      !setRef.current.has(SOUNDTRANSLATION.BackToTheFrontAnd45ForFace[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.BackToTheFrontAnd45ForFace[language];
      setRef.current.add(SOUNDTRANSLATION.BackToTheFrontAnd45ForFace[language]);
      BackToTheFrontAnd45ForFaceRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.BackToTheFrontAnd45ForFace];
      } else {
        captionRef.current =
          SoundTextMapping[Sound.BackToTheFrontAnd45ForFaceES];
      }
    }

    const nose = landmakers[0];
    const rightEar = landmakers[8];

    const isLeft = nose.x - rightEar.x > -0.1;
    const isRight = nose.x - rightEar.x < 0.035;

    if (!isLeft || !isRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const detectFaceRightPose = (poseType: PoseType, poseIndex: number) => {
    if (soundRef.current || !language) return;
    instructionRef.current = text.turn45DegreesRight[language];
    if (
      language &&
      soundRef.current !== SOUNDTRANSLATION.ContinueToTurn45Right[language] &&
      !setRef.current.has(SOUNDTRANSLATION.ContinueToTurn45Right[language])
    ) {
      soundRef.current = SOUNDTRANSLATION.ContinueToTurn45Right[language];
      setRef.current.add(SOUNDTRANSLATION.ContinueToTurn45Right[language]);
      continueToTurn45RightRef.current?.play();
      if (language === "en") {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45Right];
      } else {
        captionRef.current = SoundTextMapping[Sound.ContinueToTurn45RightES];
      }
    }

    const rightEyeOuter = landmakers[4];
    const rightEyeInner = landmakers[6];

    const isFacingRight = rightEyeOuter.x - rightEyeInner.x < 0.009;

    if (!isFacingRight) {
      secondInPosition.current = 0;
      overlaySrcRef.current = poseType.invalidImage;
      return;
    }

    inPosition(poseType, poseIndex);
  };

  const isRetakePhotoRef = useRef<boolean>(false);

  // ////////////////////////// FACE //////////////////////////

  const handleOnClickRetakePhoto = (
    e: React.MouseEvent<HTMLImageElement, globalThis.MouseEvent>,
    poseIndex: number,
  ) => {
    e.preventDefault();
    instructionRef.current = "";
    overlaySrcRef.current = "";
    isRetakePhotoRef.current = true;
    nowAdjustRef.current?.play();
    if (language === "en") {
      captionRef.current = SoundTextMapping[Sound.NowAdjust];
    } else {
      captionRef.current = SoundTextMapping[Sound.NowAdjustES];
    }
    const updatedPoses = posesToTake.map((pose, index) => {
      if (index === poseIndex) {
        return {
          ...pose,
          isPictureTaken: false,
        };
      }
      return pose;
    });
    setPosesToTake(updatedPoses);
    setIsDoneScanning(false);
    clearAllTimeOut();
  };

  // camera access error
  if (isErrorOpeningCamera || poseLandmarker === "ERROR")
    return (
      <>
        <CameraAccessError />
      </>
    );

  if (!language) return null;

  const renderSounds = () => {
    return (
      <>
        <audio
          ref={placeYourDeviceRef}
          src={SOUNDTRANSLATION.PlaceYourDevice[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={perfectNowAdjustRef}
          src={SOUNDTRANSLATION.PerfectNowAdjust[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={perfectStayStillRef}
          src={SOUNDTRANSLATION.PerfectStayStill[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={BackToTheFrontAnd45ForFaceRef}
          src={SOUNDTRANSLATION.BackToTheFrontAnd45ForFace[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={continueToTurn45LeftRef}
          src={SOUNDTRANSLATION.ContinueToTurn45Left[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={continueToTurn45LeftRef2}
          src={SOUNDTRANSLATION.ContinueToTurn45Left2[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={continueToTurn45RightRef}
          src={SOUNDTRANSLATION.ContinueToTurn45Right[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={nowTurn45LeftRef}
          src={SOUNDTRANSLATION.NowTurn45Left[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={nowTurn90LeftRef}
          src={SOUNDTRANSLATION.NowTurn90Left[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={LastOneNowTurn90LeftLeftShoulderToCameraRef}
          src={
            SOUNDTRANSLATION.LastOneNowTurn90LeftLeftShoulderToCamera[language]
          }
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={continueToTurn90RightRef}
          src={SOUNDTRANSLATION.ContinueToTurn90Right[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={NowTurn90LeftBackToCameraRef}
          src={SOUNDTRANSLATION.NowTurn90LeftBackToCamera[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={NowTurn180LeftShoulderToCameraRef}
          src={SOUNDTRANSLATION.NowTurn180LeftShoulderToCamera[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={nowAdjustRef}
          src={SOUNDTRANSLATION.NowAdjust[language]}
          onEnded={() => (soundRef.current = null)}
        />
        <audio
          ref={wereDoneRef}
          src={SOUNDTRANSLATION.WereDone[language]}
          onEnded={() => (soundRef.current = null)}
        />
      </>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        position: "relative",
        alignItems: "flex-start",
        overflowX: "auto",
        width: "100%",
      }}
    >
      {renderSounds()}
      {poseLandmarker === null && (
        <div>
          <p>Downloading AI Model.</p>
        </div>
      )}
      {poseLandmarker !== null && (
        <>
          <div
            style={{
              display: isDoneScanning ? "flex" : "none",
              flex: 1,
              gap: 15,
              flexDirection: "column",
              height: "100%",
              width: "100%",
            }}
          >
            <div
              style={{
                gap: 15,
                display: "flex",
                flexDirection: "column",
                overflowX: "auto",
                marginBottom: "auto",
              }}
            >
              {imageBlobsAndViewTypesRef.current.map(
                (imageBlobAndViewType, index) => (
                  <div className="video__screenshot-container" key={index}>
                    <img
                      className="video__screenshot"
                      src={imageBlobAndViewType.imageUrl}
                      alt={`Screenshot ${index + 1}`}
                      draggable={false}
                    />
                    <img
                      onClick={(e) => handleOnClickRetakePhoto(e, index)}
                      className="video__retake-photo"
                      src={
                        "https://clinic-os.com/flow-assets/body-scanner-ai/retake_photo_icon.png"
                      }
                      draggable={false}
                      alt="retake button"
                    />
                  </div>
                ),
              )}
            </div>
            <PrimaryButton
              onClick={() => {
                const imageBlobsAndViewTypes: TImageBlobAndViewType[] =
                  imageBlobsAndViewTypesRef.current.map((item) => {
                    return {
                      image: item.blob,
                      viewType: item.viewType,
                    };
                  });
                if (!handleSaveImageBlobsAndViewTypes) return;
                handleSaveImageBlobsAndViewTypes(imageBlobsAndViewTypes);
              }}
              fullWidth
              text={getTranslation("continue", language)}
            />
          </div>
          <div
            className="video"
            style={{ display: isDoneScanning ? "none" : "flex" }}
          >
            <div
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                zIndex: 1,
              }}
            >
              <SoundOn />
            </div>
            <div className="video__center-container">
              <video
                style={{
                  position: "relative",
                  width: "100%",
                  minWidth: 1,
                  aspectRatio: 1,
                  top: "0%",
                  left: "0%",
                }}
                className="video__video"
                autoPlay
                playsInline
                ref={videoRef}
              />
              {overlaySrcRef.current !== "" && selectedProcedure && (
                <img
                  className={
                    isFaceProcedure(selectedProcedure)
                      ? "video__overlay-face"
                      : isTorsoProcedure(selectedProcedure)
                        ? "video__overlay-torso"
                        : "video__overlay-body"
                  }
                  src={overlaySrcRef.current}
                  alt="overlay"
                />
              )}
              {threeSecondCounter !== 0 && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      background: "#000",
                      zIndex: 2,
                      top: 0,
                      bottom: 0,
                      opacity: 0.4,
                    }}
                  />
                  <p
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      translate: "-50% -50%",
                      zIndex: 3,
                      fontSize: "64px",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  >
                    {threeSecondCounter}
                  </p>
                </>
              )}
              {isTakingPicture.current && (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#FFF",
                    zIndex: 1,
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    opacity: 0.7,
                  }}
                />
              )}
              {isSettingUpCamera.current && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "4px",
                      top: "50%",
                      left: "0%",
                      zIndex: 2,
                      background: "#000",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "4px",
                      bottom: `${100 * greenlinePosition.current}%`,
                      left: 0,
                      zIndex: 3,
                      background: "#009B59",
                    }}
                  />
                  {secondInPosition.current !== 0 && (
                    <div
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        background: "#009B59",
                        zIndex: 1,
                        top: 0,
                        bottom: 0,
                        opacity: 0.3,
                      }}
                    />
                  )}
                </>
              )}
            </div>
            {!isRetakePhotoRef.current && instructionRef.current !== "" && (
              <p
                style={{
                  fontSize: "24px",
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                {instructionRef.current}
              </p>
            )}
            {isRetakePhotoRef.current && (
              <>
                <p style={{ fontSize: "24px", textAlign: "center" }}>
                  {text.text1[language]}
                </p>
              </>
            )}
            {!isRetakePhotoRef.current && isSettingUpCamera.current && (
              <p style={{ fontSize: "24px", textAlign: "center" }}>
                {text.text2[language]}
              </p>
            )}
            <NormalText
              textContent={captionRef.current}
              style={{ marginTop: "8px", textAlign: "center" }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Scan;
